import type { ReactNode } from "react";

/**
 * "There is nothing here" — said the same way everywhere, because it is one of
 * the most common things an operator reads. It says WHAT is missing (`title`),
 * says what to do about it or why it is normal (the children, optional), and
 * puts the way forward within reach (`action`, a button or a link).
 *
 *   block   a panel of its own: the body of an empty table, an empty card. The
 *           title is bold, the sentence under it is muted, the action is under
 *           that. Inside a `Table` this is what `empty` takes.
 *   inline  one muted line in a list or a card that has no room for a block
 *           (a picker with no source yet, the dashboard's cards).
 *   compact the same line one size smaller, for the places where room is
 *           counted and the text around is already small (the shell's task
 *           feed, a side panel): it never adds a notch to what was there.
 *
 * A filter that hides everything is not the same as nothing having been
 * created: say which of the two it is in the title ("No tenants match your
 * search" vs "No tenants yet"), and only offer the "create" action in the second.
 *
 * Neither variant announces itself: it is there when the page loads. A message
 * that appears after a click is a `Notice` with a role.
 */
export function EmptyState({
  title,
  children,
  action,
  size = "block",
}: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  size?: "block" | "inline" | "compact";
}) {
  if (size !== "block") {
    return (
      <p className={`${size === "compact" ? "text-xs" : "text-sm"} text-ink-muted`}>
        {title}
        {children && <> {children}</>}
        {action && <span className="ml-2">{action}</span>}
      </p>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1 px-4 py-10 text-center">
      <p className="text-sm font-semibold text-ink">{title}</p>
      {children && <p className="max-w-prose text-sm text-ink-muted">{children}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
